import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Globe02, Grid01, LayoutGrid01 } from '@untitledui/icons';
import {
    Plus,
    SearchLg,
    Eye,
    Edit01,
    Trash01,
    Package
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { DistributionChart } from '@/components/application/charts/distribution-chart';
import { containerService } from '@/data/mock-containers';
import type { Container, ContainerFilters, ContainerStatus, RiskLevel, DocumentStatus } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import WorldRoutesMapPreview from '@/components/application/map/world-routes-map-preview';
import { ContainerSpreadsheetView } from '@/components/application/containers/container-spreadsheet-view';
import { ContainerListView } from '@/components/application/containers/container-list-view';
import { Button } from '@/components/base/buttons/button';
import { useInfiniteScroll } from '@/components/application/containers/use-infinite-scroll';

type ViewMode = 'recap' | 'spreadsheet' | 'list';

export const ShipperContainersPage = () => {
    const { t } = useTranslation(['pages:containers', 'common']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const [containers, setContainers] = useState<Container[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<ContainerStatus | ''>('');
    const [riskLevelFilter, setRiskLevelFilter] = useState<RiskLevel | ''>('');
    const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatus | ''>('');
    const [shippingCompanyFilter, setShippingCompanyFilter] = useState<string | ''>('');
    const [shippingCompanyOptions, setShippingCompanyOptions] = useState<{ id: string; label: string }[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const getValidViewMode = (view: string | null): ViewMode => {
        const validModes: ViewMode[] = ['recap', 'spreadsheet', 'list'];
        return (validModes.includes(view as ViewMode) ? view : 'recap') as ViewMode;
    };
    const [viewMode, setViewMode] = useState<ViewMode>(
        getValidViewMode(searchParams.get('view'))
    );
    const [displayedCount, setDisplayedCount] = useState(20);
    const navigate = useNavigate();

    // Aggiorna query parameter quando viewMode cambia
    useEffect(() => {
        const currentView = searchParams.get('view');
        if (viewMode !== currentView) {
            const newParams = new URLSearchParams(searchParams);
            if (viewMode === 'recap') {
                newParams.delete('view');
            } else {
                newParams.set('view', viewMode);
            }
            setSearchParams(newParams, { replace: true });
        }
    }, [viewMode, searchParams, setSearchParams]);

    // Reset displayedCount quando cambia viewMode o filtri
    useEffect(() => {
        setDisplayedCount(20);
    }, [viewMode, searchTerm, statusFilter, riskLevelFilter, documentStatusFilter, shippingCompanyFilter]);

    // Container da mostrare (con lazy loading per list/table/spreadsheet)
    const displayedContainers = useMemo(() => {
        if (viewMode === 'recap') {
            // In recap mostra solo gli ultimi 10
            return containers.slice(0, 10);
        }
        // Per altre viste, mostra fino a displayedCount
        return containers.slice(0, displayedCount);
    }, [containers, displayedCount, viewMode]);

    const hasMore = displayedCount < containers.length;

    const { loadMoreRef } = useInfiniteScroll({
        hasMore,
        loadMore: () => {
            setDisplayedCount(prev => Math.min(prev + 20, containers.length));
        },
    });

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'In Transit', label: t('containerStatus.inTransit', { ns: 'types' }) },
        { id: 'Delivered', label: t('containerStatus.delivered', { ns: 'types' }) },
        { id: 'Delayed', label: t('containerStatus.delayed', { ns: 'types' }) },
        { id: 'At Port', label: t('containerStatus.atPort', { ns: 'types' }) }
    ];

    const riskLevelOptions = [
        { id: '', label: t('list.filters.allLevels') },
        { id: 'Low', label: t('riskLevel.low', { ns: 'types' }) },
        { id: 'Medium', label: t('riskLevel.medium', { ns: 'types' }) },
        { id: 'High', label: t('riskLevel.high', { ns: 'types' }) },
        { id: 'Critical', label: t('riskLevel.critical', { ns: 'types' }) }
    ];

    const documentStatusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'Completo', label: t('documentStatus.complete', { ns: 'types' }) },
        { id: 'Pending', label: t('documentStatus.pending', { ns: 'types' }) },
        { id: 'Error', label: t('documentStatus.error', { ns: 'types' }) }
    ];

    useEffect(() => {
        const loadContainers = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = localStorage.getItem('accountId') || '1';

            const filters: ContainerFilters = {
                search: searchTerm || undefined,
                status: statusFilter || undefined,
                riskLevel: riskLevelFilter || undefined,
                documentStatus: documentStatusFilter || undefined,
                shipperId: accountId,
                shippingCompany: shippingCompanyFilter || undefined
            };
            const filteredContainers = containerService.search(filters);
            setContainers(filteredContainers);
            setIsLoading(false);
        };
        // build shipping company options from all containers
        const all = containerService.getAll();
        const setOpts = Array.from(new Set(all.map(c => c.shippingCompany).filter(Boolean) as string[]))
            .sort()
            .map(v => ({ id: v, label: v }));
        setShippingCompanyOptions([{ id: '', label: t('list.filters.allCompanies') }, ...setOpts]);

        loadContainers();
    }, [searchTerm, statusFilter, riskLevelFilter, documentStatusFilter, shippingCompanyFilter]);

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    const getDocumentStatusColor = (status: string) => {
        switch (status) {
            case 'Completo': return 'success';
            case 'Pending': return 'warning';
            case 'Error': return 'error';
            default: return 'gray';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'In Transit': return 'brand';
            case 'Delivered': return 'success';
            case 'Delayed': return 'error';
            case 'At Port': return 'warning';
            default: return 'gray';
        }
    };

    // Helper per convertire color names a hex
    const getColorHex = (colorName: string): string => {
        switch (colorName) {
            case 'brand': return '#6938ef'; // brand-600
            case 'success': return '#079485'; // success-600
            case 'warning': return '#dc6803'; // warning-600
            case 'error': return '#d92d20'; // error-600
            default: return '#6b7280'; // gray-500
        }
    };

    // Calcola distribuzioni per i grafici
    const distributionData = useMemo(() => {
        const total = containers.length;

        // Risk Levels
        const riskDistribution = containers.reduce((acc, c) => {
            acc[c.riskLevel] = (acc[c.riskLevel] || 0) + 1;
            return acc;
        }, {} as Record<RiskLevel, number>);

        const riskData = [
            { label: translateRiskLevel('Low'), value: riskDistribution.Low || 0, color: getColorHex('success') },
            { label: translateRiskLevel('Medium'), value: riskDistribution.Medium || 0, color: getColorHex('warning') },
            { label: translateRiskLevel('High'), value: riskDistribution.High || 0, color: getColorHex('error') },
            { label: translateRiskLevel('Critical'), value: riskDistribution.Critical || 0, color: getColorHex('error') },
        ];

        // Container Status
        const statusDistribution = containers.reduce((acc, c) => {
            acc[c.status] = (acc[c.status] || 0) + 1;
            return acc;
        }, {} as Record<ContainerStatus, number>);

        const statusData = [
            { label: translateContainerStatus('In Transit'), value: statusDistribution['In Transit'] || 0, color: getColorHex('brand') },
            { label: translateContainerStatus('Delivered'), value: statusDistribution.Delivered || 0, color: getColorHex('success') },
            { label: translateContainerStatus('Delayed'), value: statusDistribution.Delayed || 0, color: getColorHex('error') },
            { label: translateContainerStatus('At Port'), value: statusDistribution['At Port'] || 0, color: getColorHex('warning') },
        ];

        // Document Status
        const documentDistribution = containers.reduce((acc, c) => {
            acc[c.documentStatus] = (acc[c.documentStatus] || 0) + 1;
            return acc;
        }, {} as Record<DocumentStatus, number>);

        const documentData = [
            { label: translateDocumentStatus('Completo'), value: documentDistribution.Completo || 0, color: getColorHex('success') },
            { label: translateDocumentStatus('Pending'), value: documentDistribution.Pending || 0, color: getColorHex('warning') },
            { label: translateDocumentStatus('Error'), value: documentDistribution.Error || 0, color: getColorHex('error') },
        ];

        return { riskData, statusData, documentData, total };
    }, [containers, translateRiskLevel, translateContainerStatus, translateDocumentStatus]);

    const handleDelete = async (id: string) => {
        if (window.confirm(t('messages.confirmDelete', { ns: 'common' }))) {
            const success = containerService.delete(id);
            if (success) {
                // Reload containers
                const accountId = localStorage.getItem('accountId') || '1';
                const filteredContainers = containerService.search({ shipperId: accountId });
                setContainers(filteredContainers);
            }
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('list.subtitle')}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-lg bg-secondary p-1 ring-1 ring-secondary_alt">
                        <Button
                            size="sm"
                            color={viewMode === 'recap' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('recap')}
                            iconLeading={LayoutGrid01}
                        >
                            Recap
                        </Button>
                        <Button
                            size="sm"
                            color={viewMode === 'spreadsheet' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('spreadsheet')}
                            iconLeading={Grid01}
                        >
                            Spreadsheet
                        </Button>
                        <Button
                            size="sm"
                            color={viewMode === 'list' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('list')}
                            iconLeading={Package}
                        >
                            Lista
                        </Button>
                        <Button
                            size="sm"
                            color="secondary"
                            onClick={() => navigate('/shipper/containers/world-map')}
                            iconLeading={Globe02}
                        >
                            {t('list.viewMap')}
                        </Button>
                    </div>
                    <Link
                        to="/shipper/containers/new"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                    >
                        <Plus className="size-5" />
                        {t('list.newContainer')}
                    </Link>
                </div>
            </div>

            {/* World Routes Map Preview e Distribution Charts - solo in modalità Recap */}
            {viewMode === 'recap' && (
                <>
                    {/* World Routes Map Preview */}
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-semibold text-primary">{t('worldMap.title')}</h3>
                                <p className="text-sm text-tertiary">{t('worldMap.updateInfo')}</p>
                            </div>
                            <Link
                                to="/shipper/containers/world-map"
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                            >
                                <Globe02 className="size-4" />
                                {t('list.viewMap')}
                            </Link>
                        </div>
                        <WorldRoutesMapPreview height={360} />
                    </div>

                    {/* Distribution Charts */}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <DistributionChart
                            title={t('list.charts.riskLevels', { ns: 'pages:containers' }) || 'Risk Levels Distribution'}
                            data={distributionData.riskData}
                            total={distributionData.total}
                        />
                        <DistributionChart
                            title={t('list.charts.containerStatus', { ns: 'pages:containers' }) || 'Container Status Distribution'}
                            data={distributionData.statusData}
                            total={distributionData.total}
                        />
                        <DistributionChart
                            title={t('list.charts.documentStatus', { ns: 'pages:containers' }) || 'Document Status Distribution'}
                            data={distributionData.documentData}
                            total={distributionData.total}
                        />
                    </div>
                </>
            )}

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
                    <Input
                        placeholder={t('list.filters.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.filters.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as ContainerStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.riskLevelPlaceholder')}
                        items={riskLevelOptions}
                        selectedKey={riskLevelFilter}
                        onSelectionChange={(key) => setRiskLevelFilter(key as RiskLevel | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.documentStatusPlaceholder')}
                        items={documentStatusOptions}
                        selectedKey={documentStatusFilter}
                        onSelectionChange={(key) => setDocumentStatusFilter(key as DocumentStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.shippingCompanyPlaceholder')}
                        items={shippingCompanyOptions}
                        selectedKey={shippingCompanyFilter}
                        onSelectionChange={(key) => setShippingCompanyFilter(key as string | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Containers View */}
            <div key={viewMode}>
                {viewMode === 'recap' ? (
                    <div className="space-y-6">
                        {/* Ultimi 10 container */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-primary">
                                    Ultimi 10 Container ({displayedContainers.length})
                                </h3>
                                <Link
                                    to="/shipper/containers"
                                    className="text-sm text-brand-secondary hover:text-brand-secondary_hover"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setViewMode('spreadsheet');
                                    }}
                                >
                                    Vedi tutti →
                                </Link>
                            </div>
                            <TableCard.Root>
                                <Table>
                                    <Table.Header>
                                        <Table.Head label={t('list.table.headers.containerNumber')} isRowHeader />
                                        <Table.Head label={t('list.table.headers.route')} />
                                        <Table.Head label="Shipping Company" />
                                        <Table.Head label={t('list.table.headers.progress')} />
                                        <Table.Head label="ETA" />
                                        <Table.Head label={t('list.table.headers.status')} />
                                        <Table.Head label={t('list.table.headers.actions')} />
                                    </Table.Header>
                                    <Table.Body>
                                        {displayedContainers.map((container) => (
                                            <Table.Row key={container.id}>
                                                <Table.Cell>
                                                    <div className="font-medium text-primary">{container.containerNumber}</div>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div className="text-sm text-tertiary">
                                                        {container.route.from} → {container.route.to}
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div className="text-sm text-tertiary">{container.shippingCompany ?? '-'}</div>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div className="flex items-center gap-2">
                                                        <ProgressBar
                                                            value={container.progress}
                                                            min={0}
                                                            max={100}
                                                        />
                                                        <span className="text-sm text-tertiary">{container.progress}%</span>
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <span className="text-sm text-tertiary">
                                                        {container.eta.toLocaleDateString('it-IT')}
                                                    </span>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={getStatusColor(container.status)} size="sm">
                                                        {translateContainerStatus(container.status)}
                                                    </Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div className="flex items-center gap-2">
                                                        <Link
                                                            to={(() => {
                                                                const currentView = searchParams.get('view');
                                                                return currentView 
                                                                    ? `/shipper/containers/${container.id}?view=${currentView}`
                                                                    : `/shipper/containers/${container.id}`;
                                                            })()}
                                                            className="text-brand-secondary hover:text-brand-secondary_hover"
                                                        >
                                                            <Eye className="size-4" />
                                                        </Link>
                                                    </div>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </TableCard.Root>
                        </div>
                    </div>
                ) : viewMode === 'spreadsheet' ? (
                    <>
                        <ContainerSpreadsheetView
                            containers={displayedContainers}
                            totalCount={containers.length}
                            onUpdate={(_containerId, _field, _value) => {
                                // Ricarica containers dopo aggiornamento
                                const accountId = localStorage.getItem('accountId') || '1';
                                const filters: ContainerFilters = {
                                    search: searchTerm || undefined,
                                    status: statusFilter || undefined,
                                    riskLevel: riskLevelFilter || undefined,
                                    documentStatus: documentStatusFilter || undefined,
                                    shipperId: accountId,
                                    shippingCompany: shippingCompanyFilter || undefined
                                };
                                const filteredContainers = containerService.search(filters);
                                setContainers(filteredContainers);
                            }}
                            onContainerClick={(containerId) => {
                                const currentView = searchParams.get('view');
                                const url = currentView 
                                    ? `/shipper/containers/${containerId}?view=${currentView}`
                                    : `/shipper/containers/${containerId}`;
                                navigate(url);
                            }}
                        />
                        {hasMore && (
                            <div ref={loadMoreRef} className="flex justify-center py-4">
                                <div className="text-sm text-tertiary">Caricamento...</div>
                            </div>
                        )}
                    </>
                ) : viewMode === 'list' ? (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-primary">
                                {t('list.table.title')} ({containers.length})
                            </h3>
                        </div>
                        <ContainerListView
                            containers={displayedContainers}
                            onContainerSelect={(id) => {
                                // Navigate to container detail preserving view
                                const currentView = searchParams.get('view');
                                const url = currentView 
                                    ? `/shipper/containers/${id}?view=${currentView}`
                                    : `/shipper/containers/${id}`;
                                navigate(url);
                            }}
                        />
                        {hasMore && (
                            <div ref={loadMoreRef} className="flex justify-center py-4">
                                <div className="text-sm text-tertiary">Caricamento...</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <TableCard.Root>
                            <TableCard.Header
                                title={`${t('list.table.title')} (${containers.length})`}
                                description={t('list.table.description')}
                            />

                            <Table>
                                <Table.Header>
                                    <Table.Head label={t('list.table.headers.containerNumber')} isRowHeader />
                                    <Table.Head label={t('list.table.headers.route')} />
                                    <Table.Head label="Shipping Company" />
                                    <Table.Head label={t('list.table.headers.progress')} />
                                    <Table.Head label="ETA" />
                                    <Table.Head label={t('list.table.headers.status')} />
                                    <Table.Head label={t('list.table.headers.documentStatus')} />
                                    <Table.Head label={t('list.table.headers.riskLevel')} />
                                    <Table.Head label={t('list.table.headers.value')} />
                                    <Table.Head label={t('list.table.headers.actions')} />
                                </Table.Header>
                                <Table.Body>
                                    {displayedContainers.map((container) => (
                                        <Table.Row key={container.id}>
                                            <Table.Cell>
                                                <div className="font-medium text-primary">{container.containerNumber}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="text-sm text-tertiary">
                                                    {container.route.from} → {container.route.to}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="text-sm text-tertiary">{container.shippingCompany ?? '-'}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <ProgressBar
                                                        value={container.progress}
                                                        min={0}
                                                        max={100}
                                                    />
                                                    <span className="text-sm text-tertiary">{container.progress}%</span>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {container.eta.toLocaleDateString('it-IT')}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={getStatusColor(container.status)} size="sm">
                                                    {translateContainerStatus(container.status)}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                                    {translateDocumentStatus(container.documentStatus)}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                                    {translateRiskLevel(container.riskLevel)}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {container.value.currency} {container.value.amount.toLocaleString()}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Link
                                                        to={(() => {
                                                            const currentView = searchParams.get('view');
                                                            return currentView 
                                                                ? `/shipper/containers/${container.id}?view=${currentView}`
                                                                : `/shipper/containers/${container.id}`;
                                                        })()}
                                                        className="text-brand-secondary hover:text-brand-secondary_hover"
                                                    >
                                                        <Eye className="size-4" />
                                                    </Link>
                                                    <Link
                                                        to={`/shipper/containers/${container.id}/edit`}
                                                        className="text-tertiary hover:text-primary"
                                                    >
                                                        <Edit01 className="size-4" />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleDelete(container.id)}
                                                        className="text-error-600 hover:text-error-700"
                                                    >
                                                        <Trash01 className="size-4" />
                                                    </button>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                        {hasMore && (
                            <div ref={loadMoreRef} className="flex justify-center py-4">
                                <div className="text-sm text-tertiary">Caricamento...</div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {containers.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <Package className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">{t('list.table.noContainers.title')}</h3>
                        <p className="text-tertiary">
                            {searchTerm || statusFilter || riskLevelFilter || documentStatusFilter
                                ? t('list.table.noContainers.message')
                                : t('list.table.noContainers.emptyMessage')
                            }
                        </p>
                    </div>
                    {!searchTerm && !statusFilter && !riskLevelFilter && !documentStatusFilter && (
                        <Link
                            to="/shipper/containers/new"
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                        >
                            <Plus className="size-5" />
                            {t('list.table.noContainers.createFirst')}
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};
